import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';
import type { CartStockRemoval, CartUrlNotice } from '../types/models';
import { useShopifyCartService } from '../services/shopifyCart';
import { freeGiftProduct } from '@/features/Page/data/globalData';
import { resolveRegionValue } from '@/composables/regionHelpers';

export const useCartStore = defineStore('cartStore', () => {
  const { DEFAULT_EMPTY_CART } = useShopifyCartService();
  const cartData = ref<ShopifyCart>(DEFAULT_EMPTY_CART());
  const cartDataLoaded = ref(false);
  const pendingCartMutations = ref(0);
  const cartUpdateInProgress = computed(() => pendingCartMutations.value > 0);
  const error = ref<string | null>(null);
  const cartAdjustedForStock = ref(false);
  const cartStockRemovals = ref<CartStockRemoval[]>([]);
  const cartAddLimitedByStock = ref(false);
  const cartAdjustedBeforeLatestAdd = ref(false);
  const latestAddLineIdentities = ref<string[]>([]);
  const cartUrlNotice = ref<CartUrlNotice | null>(null);
  const cartUrlActionPending = ref(false);

  const resolvedFreeGiftProduct = computed(() => resolveRegionValue(freeGiftProduct));
  const freeGiftCartSku = computed(
    () =>
      resolvedFreeGiftProduct.value?.cartSku ?? resolvedFreeGiftProduct.value?.displaySku ?? null,
  );

  const isFreeGiftItem = (item: ShopifyCartItem) => {
    return (
      (!!freeGiftCartSku.value && item.sku === freeGiftCartSku.value) ||
      item.properties?._free_gift === 'true'
    );
  };

  const isFreeGiftSku = (sku?: string | null) => {
    return !!sku && !!freeGiftCartSku.value && sku === freeGiftCartSku.value;
  };

  const itemCount = computed<number>(() => cartData.value.item_count);
  const totalPrice = computed<number>(() => cartData.value.total_price);
  const items = computed<ShopifyCartItem[]>(() => cartData.value.items);
  const isEmpty = computed<boolean>(() => itemCount.value === 0);
  const subtotal = computed<number>(() => cartData.value.items_subtotal_price);
  const totalDiscount = computed<number>(() => cartData.value.total_discount);

  const MAX_QUANTITY = 5;

  const startCartMutation = () => {
    pendingCartMutations.value += 1;
  };

  const finishCartMutation = () => {
    pendingCartMutations.value = Math.max(0, pendingCartMutations.value - 1);
  };

  const setCartAdjustedForStock = (isAdjusted: boolean) => {
    cartAdjustedForStock.value = isAdjusted;
    if (!isAdjusted) cartStockRemovals.value = [];
  };

  const addCartStockRemovals = (removals: CartStockRemoval[]) => {
    cartAdjustedForStock.value = true;
    removals.forEach(({ title, quantity, unavailableComponentTitles = [] }) => {
      if (!title) return;
      const existingRemoval = cartStockRemovals.value.find((removal) => removal.title === title);
      if (!existingRemoval) {
        cartStockRemovals.value.push({ title, quantity, unavailableComponentTitles });
        return;
      }
      existingRemoval.quantity += quantity;
      existingRemoval.unavailableComponentTitles = [
        ...new Set([
          ...(existingRemoval.unavailableComponentTitles ?? []),
          ...unavailableComponentTitles,
        ]),
      ];
    });
  };

  const setCartAddLimitedByStock = (isLimited: boolean) => {
    cartAddLimitedByStock.value = isLimited;
  };

  const startCartAdd = () => {
    cartAddLimitedByStock.value = false;
    cartAdjustedBeforeLatestAdd.value = cartAdjustedForStock.value;
  };

  const setLatestAddLineIdentities = (lineIdentities: string[]) => {
    latestAddLineIdentities.value = lineIdentities;
  };

  const setCartUrlNotice = (notice: CartUrlNotice | null) => {
    cartUrlNotice.value = notice;
  };

  const setCartUrlActionPending = (isPending: boolean) => {
    cartUrlActionPending.value = isPending;
  };

  const setCart = (newCart: ShopifyCart): ShopifyCart => {
    cartData.value = newCart;
    if (!cartDataLoaded.value) cartDataLoaded.value = true;
    return cartData.value;
  };

  const cartArray = computed(() => {
    return items.value.map((item) => {
      const customBundleDetails = item.properties?._custom_bundle_details;
      let bundleSkus: string[] = [];
      if (customBundleDetails) {
        try {
          bundleSkus = JSON.parse(customBundleDetails).map((b: { sku: string }) => b.sku);
        } catch {
          console.warn('cart: failed to parse _custom_bundle_details', {
            sku: item.sku,
            key: item.key,
            customBundleDetails,
          });
        }
      }
      return {
        sku: item.sku,
        handle: item.handle,
        product_id: item.product_id?.toString(),
        variant_id: item.variant_id?.toString(),
        quantity: item.quantity,
        bundleSkus,
        isFreeGift: isFreeGiftItem(item),
      };
    });
  });

  const skuQuantities = computed(() => {
    const map = new Map<string, number>();
    cartArray.value.forEach((item) => {
      if (item.sku) {
        map.set(item.sku, (map.get(item.sku) || 0) + item.quantity);
      }
      item.bundleSkus.forEach((sku) => {
        map.set(sku, (map.get(sku) || 0) + item.quantity);
      });
    });
    return map;
  });

  const allSkusInCart = computed(() => {
    return new Set(cartArray.value.flatMap((item) => [item.sku, ...item.bundleSkus]));
  });
  const freeGiftItem = computed(() => items.value.find(isFreeGiftItem) ?? null);

  const paidNonGiftItems = computed(() =>
    items.value.filter((item) => !isFreeGiftItem(item) && item.line_price > 0),
  );

  const cartHasPaidNonGiftItems = computed(() => paidNonGiftItems.value.length > 0);

  const cartHasSku = (sku: string | string[]) => {
    const targets = Array.isArray(sku) ? sku : [sku];
    return targets.some((s) => (skuQuantities.value.get(s) || 0) > 0);
  };

  const cartHasSkuQuantity = (sku: string, requiredQuantity: number) => {
    return (skuQuantities.value.get(sku) || 0) >= requiredQuantity;
  };

  const getSkuQuantity = (sku: string) => {
    return skuQuantities.value.get(sku) || 0;
  };

  return {
    cartData,
    cartDataLoaded,
    cartUpdateInProgress,
    error,
    itemCount,
    totalPrice,
    items,
    isEmpty,
    subtotal,
    totalDiscount,
    MAX_QUANTITY,
    resolvedFreeGiftProduct,
    freeGiftItem,
    paidNonGiftItems,
    cartHasPaidNonGiftItems,
    isFreeGiftSku,
    isFreeGiftItem,
    setCart,
    startCartMutation,
    finishCartMutation,
    cartAdjustedForStock,
    setCartAdjustedForStock,
    cartStockRemovals,
    addCartStockRemovals,
    cartAddLimitedByStock,
    setCartAddLimitedByStock,
    cartAdjustedBeforeLatestAdd,
    startCartAdd,
    latestAddLineIdentities,
    setLatestAddLineIdentities,
    cartUrlNotice,
    setCartUrlNotice,
    cartUrlActionPending,
    setCartUrlActionPending,
    cartArray,
    allSkusInCart,
    cartHasSku,
    cartHasSkuQuantity,
    getSkuQuantity,
  };
});
